import { Box, CircularProgress, InputAdornment } from "@mui/material";
import React, { useEffect, useState } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HeroList from "../components/login/HeroList";
import { useUserActions } from "../hooks/useUserActions";
import { useBanner } from "../context/BannerContext";
import { getTenant, setTenant } from "../../utils/tenant";
import { LOGIN_ERROR, LOGIN_SUCCESS } from "../constants";
import { HeaderText } from "../shared/text/HeaderText.styled";
import { BodyText } from "../shared/text/BodyText.styled";
import { InputField } from "../shared/inputs/InputField.styled";
import { PrimaryButton } from "../shared/buttons/PrimaryButton.styled";
import PaneTooltip, { INFO_TOOLTIP_DELAY_MS } from "../shared/text/PaneTooltip";
import { ediphiPalette } from "../styles/colors";

interface LoginPageProps {
  onLoginSuccess: () => void;
}

const TENANT_DOMAIN = process.env.TENANT_DOMAIN || "ediphi.com";

// Excel Desktop's sign-in window can't open the local stack's plain-http Keycloak, so local
// Desktop builds sign in with a session token copied from the local web app instead.
const signsInWithPastedToken = () =>
  process.env.LOCAL === "true" && Office.context.platform !== Office.PlatformType.OfficeOnline;

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const [tenantInputValue, setTenantInputValue] = useState<string>("");
  const [tokenInputValue, setTokenInputValue] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const message = "Seamlessly Sync Your Ediphi Data with Excel";
  const { showBanner } = useBanner();
  const { handleLogin, handleTokenLogin } = useUserActions();
  const pasteToken = signsInWithPastedToken();

  useEffect(() => {
    const storedTenant = getTenant();
    if (storedTenant) setTenantInputValue(storedTenant);
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (tenantInputValue) setTenant(tenantInputValue);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [tenantInputValue]);

  const handleTenantChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value.toLowerCase();
    setTenantInputValue(newValue);
  };

  const handleLoginClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    // Holding Shift while clicking bypasses the tenant's SSO redirect and shows Keycloak's
    // username/password form directly (employee login).
    const bypassSso = event.shiftKey;
    // The debounced effect above may not have run yet, and handleLogin reads the tenant
    // synchronously — without this a fast first-time user gets "Login failed" and no window.
    localStorage.setItem("tenant", tenantInputValue);

    setBusy(true);
    try {
      const loggedIn = pasteToken ? await handleTokenLogin(tokenInputValue) : await handleLogin(bypassSso);
      if (!loggedIn) return;
      showBanner(LOGIN_SUCCESS, "success");
      onLoginSuccess();
    } catch (error) {
      showBanner(error instanceof Error && error.message ? error.message : LOGIN_ERROR, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, py: 2.5, width: "100%" }}>
      <HeaderText sx={{ fontSize: "24px", lineHeight: "32px", textAlign: "center" }}>{message}</HeaderText>

      <HeroList />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
          <HeaderText sx={{ color: ediphiPalette.mainTextColor }}>Enter your company to begin</HeaderText>
          <PaneTooltip
            enterDelay={INFO_TOOLTIP_DELAY_MS}
            title={
              <Box sx={{ maxWidth: 230 }}>
                Your company is identified by the URL you use to access Ediphi:
                <br />
                {`https://{your-company}.${TENANT_DOMAIN}`}
              </Box>
            }
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: ediphiPalette.brand500, cursor: "pointer" }} />
            </Box>
          </PaneTooltip>
        </Box>

        <InputField
          sx={{
            backgroundColor: "#FFF",
            width: "65%",
            alignSelf: "center",
            "& .MuiInputBase-input": { textAlign: "right" },
          }}
          size="small"
          variant="outlined"
          placeholder="your-company"
          value={tenantInputValue}
          onChange={handleTenantChange}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <BodyText sx={{ whiteSpace: "nowrap" }}>.{TENANT_DOMAIN}</BodyText>
                </InputAdornment>
              ),
            },
          }}
        />

        {pasteToken && (
          <InputField
            sx={{ backgroundColor: "#FFF", width: "65%", alignSelf: "center" }}
            size="small"
            variant="outlined"
            placeholder="Session token from the local web app"
            value={tokenInputValue}
            onChange={(event) => setTokenInputValue(event.target.value)}
          />
        )}
      </Box>

      <PrimaryButton
        size="large"
        sx={{ alignSelf: "center", minWidth: "70px" }}
        onClick={handleLoginClick}
        disabled={busy || !tenantInputValue || (pasteToken && !tokenInputValue)}
      >
        {busy ? <CircularProgress size={18} color="inherit" /> : "Login"}
      </PrimaryButton>
    </Box>
  );
};

export default LoginPage;
